import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Row, Col, ListGroup, ListGroupItem, Button, ButtonGroup, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useAuth } from '../../../../Context/Auth/AuthContext';
import { toast } from 'react-toastify';
import PSO from '../../../../services/PSO';
import Strings from '../../../../translations/Translations';
import UserStatuses from '../../../../enums/UserStatuses';

const avatarSrc = process.env.REACT_APP_PZ_INPUT_USER_AVATAR_SRC;
const REMIX_URL = process.env.REACT_APP_PZ_REMIX_URL;

export default function Users(props) {
  const { currentIt } = props;

  const { authenticated, toggleLoading } = useAuth();
  const [inputUsers, setInputUsers] = useState([]);
  const [currentFilter, setCurrentFilter] = useState(UserStatuses[0]);

  const getUsers = useCallback(async (key = 0) => {
    toggleLoading(true);

    try {
      let response;

      if (key === 0) {
        response = await PSO.getInputUsersInRemix({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid,
          etype: 1,
          getblockeduserinfo: 1,
          getauthorizeduserinfo: 1
        });
      } else if (key === 1) {
        response = await PSO.getBlockedInputUserListByRemix({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid
        });
      } else if (key === 2) {
        response = await PSO.getAuthorizedInputUserListByRemix({
          un: authenticated.un,
          al: authenticated.al,
          rid: currentIt.rid
        });
      }

      if (response.result === 'success') {
        if (response.its) {
          const its = [];

          response.its.forEach((val) => {
            if (val.inpun) {
              val.un = val.inpun;
            } else if (val.blckun) {
              val.un = val.blckun;
            } else if (val.authun) {
              val.un = val.authun;
            }

            if (key === 1) {
              val.rblku = 1;
            } else if (key === 2) {
              val.rauthu = 1;
            }

            its.push(val);
          });

          setInputUsers(its);
        }
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [currentIt, authenticated, toggleLoading, setInputUsers]);

  const applyFilter = useCallback(async (value) => {
    if (value.key !== currentFilter.key) {
      if (value.key !== 3) {
        setCurrentFilter(value);
        getUsers(value.key);
      }
    }
  }, [setCurrentFilter, currentFilter, getUsers]);

  const blockUser = useCallback(async (user) => {
    toggleLoading(true);

    try {
      const response = await PSO.blockInputUserForRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        blckun: user
      });

      if (response.result === 'success') {
        getUsers(currentFilter.key);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, currentIt, toggleLoading, getUsers, currentFilter]);

  const unblockUser = useCallback(async (user) => {
    toggleLoading(true);

    try {
      const response = await PSO.unblockInputUserForRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        blckun: user
      });

      if (response.result === 'success') {
        getUsers(currentFilter.key);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, currentIt, toggleLoading, getUsers, currentFilter]);

  const authorizeUser = useCallback(async (user) => {
    toggleLoading(true);

    try {
      const response = await PSO.authorizeInputUserForRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        authun: user
      });

      if (response.result === 'success') {
        getUsers(currentFilter.key);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, currentIt, toggleLoading, getUsers, currentFilter]);

  const unauthorizeUser = useCallback(async (user) => {
    toggleLoading(true);

    try {
      const response = await PSO.unauthorizeInputUserForRemix({
        un: authenticated.un,
        al: authenticated.al,
        rid: currentIt.rid,
        authun: user
      });

      if (response.result === 'success') {
        getUsers(currentFilter.key);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, currentIt, toggleLoading, getUsers, currentFilter]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  return (
    <Fragment>
      <Row className='mb-3'>
        <Col>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="light">
              {currentFilter.label}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem header>{Strings('filter_users')}</DropdownItem>
              {UserStatuses.map((value) => (
                <DropdownItem
                  key={value.key}
                  onClick={() => applyFilter(value)}
                >
                  {value.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </Col>
      </Row>

      <Row>
        <Col>
          {inputUsers && inputUsers.length === 0 && (
            <div className="mt-2 alert alert-info fade show" role="alert">
              {Strings('no_users_here')}
            </div>
          )}

          <ListGroup flush>
            {inputUsers && inputUsers.map((value) => (
              <ListGroupItem key={value.un}>
                <div className="widget-content p-0">
                  <div className="widget-content-wrapper">
                    <div className="widget-content-left mr-3">
                      <a href={`${REMIX_URL}?u=${value.un}`} target='_blank' rel='noreferrer noopener'>
                        <img
                          width={42}
                          className="rounded-circle"
                          src={avatarSrc.replace('{username}', value.un)}
                          alt="" />
                      </a>
                    </div>
                    <div className="widget-content-left">
                      <div className="widget-heading">
                        <a
                          href={`${REMIX_URL}?u=${value.un}`}
                          target='_blank' rel='noreferrer noopener'
                          className='text-decoration-none text-reset'
                        >
                          {value.un}
                        </a>
                      </div>
                      <div>
                        {value.un === authenticated.un && (
                          <div className="mr-2 badge badge-success">
                            {Strings('owner')}
                          </div>
                        )}
                        {value.un !== authenticated.un && (
                          <Fragment>
                            {typeof value.rauthu !== undefined && value.rauthu === 1 && (
                              <div className="mr-2 badge badge-primary">
                                {Strings('authorized')}
                              </div>
                            )}
                            {typeof value.rblku !== undefined && value.rblku === 1 && (
                              <div className="mr-2 badge badge-danger">
                                {Strings('blocked')}
                              </div>
                            )}
                          </Fragment>
                        )}
                      </div>
                    </div>
                    <div className="widget-content-right">
                      {value.un !== authenticated.un && (
                        <ButtonGroup size="md">
                          {typeof value.rauthu !== undefined && value.rauthu === 0 && typeof value.rblku !== undefined && value.rblku === 0 && (
                            <Button
                              color="primary"
                              outline
                              onClick={() => authorizeUser(value.un)}
                            >
                              {Strings('authorize')}
                            </Button>
                          )}
                          {typeof value.rauthu !== undefined && value.rauthu === 1 && (
                            <Button
                              color="primary"
                              outline
                              onClick={() => unauthorizeUser(value.un)}
                            >
                              {Strings('unauthorize')}
                            </Button>
                          )}
                          {typeof value.rblku !== undefined && value.rblku === 0 && (
                            <Button
                              color="danger"
                              outline
                              onClick={() => blockUser(value.un)}
                            >
                              {Strings('block')}
                            </Button>
                          )}
                          {typeof value.rblku !== undefined && value.rblku === 1 && (
                            <Button
                              color="danger"
                              outline
                              onClick={() => unblockUser(value.un)}
                            >
                              {Strings('unblock')}
                            </Button>
                          )}
                        </ButtonGroup>
                      )}
                    </div>
                  </div>
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        </Col>
      </Row>
    </Fragment>
  );
}
